import { RatingsMode } from '@/shared/types/analytics';
import { getGraphQLSet } from '@/util/util';

export type { RatingsMode };

export const ALL_RATINGS = [1, 2, 3, 4, 5];

/**
 * Stars matched by a CSAT filter.
 *
 * `in` holds the stars directly; an older saved filter holds a threshold under
 * `ratings` but the stars it matched under `csatBrain`.
 *
 * Returns `undefined` when nothing is selected, and an empty array when the
 * threshold excludes every star or is not a number. Those two are not the
 * same thing: the first means "do not filter", the second means "nothing can
 * match". A malformed threshold still renders as an active filter chip, so it
 * must not fall into the unfiltered branch.
 */
export const expandRatings = (
  ratings: (string | number)[] | undefined,
  mode: RatingsMode | undefined
): number[] | undefined => {
  if (!ratings || ratings.length === 0) {
    return undefined;
  }
  if (mode === 'in') {
    return ratings.map(Number).filter((star) => ALL_RATINGS.includes(star));
  }
  const threshold = Number(ratings[0]);
  if (Number.isNaN(threshold)) {
    return [];
  }
  const effectiveMode = mode ?? 'moreThan';
  if (effectiveMode === 'exact') {
    return [threshold];
  }
  if (effectiveMode === 'lessThan') {
    return ALL_RATINGS.filter((r) => r < threshold);
  }
  return ALL_RATINGS.filter((r) => r > threshold);
};

/**
 * Wire value for the `ratings` / `csat_brain` / `csat_human_agent` SQL args,
 * which are all matched as `arg IS NULL OR column = ANY(arg)`.
 *
 * `null` leaves the query unfiltered. An empty set literal filters everything
 * out, because `column = ANY('{}')` is false — the distinction keeps an
 * impossible threshold from silently returning every conversation.
 */
export const ratingsFilterToSet = (
  ratings: (string | number)[] | undefined,
  mode: RatingsMode | undefined
): string | null => {
  const expanded = expandRatings(ratings, mode);
  if (!expanded) {
    return null;
  }
  return expanded.length === 0 ? '{}' : getGraphQLSet(expanded);
};
